import { Button, Card, Input, message, Row, Table, TableProps, Tooltip } from 'antd';
import React, { useEffect } from 'react'
import { FileAddOutlined, FolderOpenOutlined, SyncOutlined } from '@ant-design/icons';
import { useUserContext } from '../../context/UserContext';
import { createPricingDeal, getHubspotDeals, getPricingDeals, refreshDeals } from '../../services/Apis';
import { useNavigate } from 'react-router-dom';
import DealEconomicsSummariesTable from './DealEconomicsSummariesTable';
import dayjs from 'dayjs';
import { currencySymbolsMap } from '../../context/CurrencyContext';

const { Search } = Input;

interface DataType {
    expandableRow: any,
    key: string,
    dealId: string,
    client: string,
    dealName: string,
    startDate: string,
    Stage: string,
    dealAmount: string,
    dealOwner: string,
    HubSpotLink: string,
    canCreatePricing: boolean,
    duration: number,
    odeStatus: string,
    updatedOn: string,
    workAhead: string,
    unlockRequestReason: string,
    unlockComments?: string,
    currency: string,
}

interface DealData {
    final_version_name: any;
    is_updated_after_lock: any;
    is_pricing_contributor: any;
    can_edit_pricing: any;
    expandable_row: boolean;
    deal_owner: string;
    deal_stage: string;
    deal_id: number;
    deal_name: string;
    deal_owner_name: string;
    company_name: string;
    client_name: string;
    project_start_date: string;
    start_date: string;
    updated_on: string;
    hubspot_link: string;
    deal_amount: number;
    deal_stage_name: string;
    can_create_pricing: boolean;
    is_owner: boolean;
    duration_in_months: number;
    is_version_locked: boolean;
    work_ahead: string,
    unlock_request_reason: string,
    unlock_comments?: string,
    currency: string,
}

interface NoPricingDealsTableProps {
  displayUserSpecificDeals: boolean;
}

const AllPricingDealsTable: React.FC<NoPricingDealsTableProps> =({displayUserSpecificDeals = false}) => {
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [deals, setDeals] = React.useState<DataType[]>([]);
    const [filteredDeals, setFilteredDeals] = React.useState(deals);
    const [loading, setLoading] = React.useState<boolean>(true);
    const { userContext, setUserContext } = useUserContext();
    const [tokenExpired, setTokenExpired] = React.useState<boolean>(false);
    const navigate = useNavigate();
    const [refreshingDeal, setRefreshingDeal] = React.useState<string>('');
    const [disableCreateButton, setDisableCreateButton] = React.useState<boolean>(false);
    const [expandedRowKeys, setExpandedRowKeys] = React.useState<any[]>([]);

    const handleExpand = (key: string) => {
        setExpandedRowKeys((prevExpandedRowKeys) => {
            const index = prevExpandedRowKeys.indexOf(key);
            if (index === -1) {
                return [...prevExpandedRowKeys, key];
            } else {
                return prevExpandedRowKeys.filter((k) => k !== key);
            }
        });
    };

    const deepSearch = (data: DataType[], searchTerm: string): DataType[] => {
        return data.filter((item) =>
          Object.values(item).some(
            (value) =>
              (typeof value === "string" &&
                value.toLowerCase().includes(searchTerm.toLowerCase())) ||
              (Array.isArray(value) &&
                value.some(
                  (element) =>
                    typeof element === "string" &&
                    element.toLowerCase().includes(searchTerm.toLowerCase())
                )) ||
              (typeof value === "object" &&
                value !== null &&
                deepSearch([value], searchTerm).length > 0)
          )
        );
      };
    
      const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const term = event.target.value;
        setSearchTerm(term);
        if (term) {
          const results = deepSearch(deals, term);
          setFilteredDeals(results);
        } else {
          setFilteredDeals(deals);
        }
      };

    const formatCurrency = (amount: number, currency: string): string => {
        try {
            return `${currencySymbolsMap[currency] || '$'} ${Math.round(amount)?.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
        } catch (error) {
            console.error(error);
            return '$ 0';
        }
    };

    const transformDeal = (deal: DealData[]): DataType[] => {
        try {
            const transformedData: DataType[] = deal.map((deal: DealData, index: number) => ({
                key: String(index), // Convert deal_id to string
                dealId: String(deal.deal_id), // Assuming 'dealId' should be the final_version_id
                client: !deal.expandable_row ? deal.company_name : deal.client_name,
                dealName: deal.deal_name,
                startDate: !deal.expandable_row ? deal.project_start_date ? new Date(deal.project_start_date).toLocaleDateString('en-US'): deal.project_start_date : deal.start_date ? new Date(deal.start_date).toLocaleDateString('en-US') : deal.start_date, // Format date
                Stage: !deal.expandable_row ? deal.deal_stage_name : deal.deal_stage,
                latestVersion: [deal.final_version_name],
                dealAmount: formatCurrency(deal.deal_amount, deal.currency), // Format amount
                dealOwner:!deal.expandable_row ? deal.deal_owner_name : deal.deal_owner,
                updatedOn: deal.expandable_row ? dayjs(deal.updated_on).tz('America/New_York').format('MMM D, YYYY, h:mm:ss A z') : '',
                HubSpotLink: deal.hubspot_link,
                canCreatePricing: deal.can_create_pricing,
                canEditPricing: deal.can_edit_pricing,
                isOwner: deal.is_owner,
                isPricingContributor: deal.is_pricing_contributor,
                isDealUpdatedAfterLock: deal.is_updated_after_lock,
                duration: deal.duration_in_months,
                odeStatus: deal.is_version_locked ? 'Submitted' : deal.expandable_row ? 'Saved, not submitted': "Pricing not created",
                expandableRow: deal.expandable_row ?? false,
                workAhead: deal.work_ahead,
                unlockRequestReason: deal.unlock_request_reason,
                unlockComments: deal.unlock_comments,
                currency: deal.currency
            }))
            return transformedData;
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const createDealPricing = async (dealId: number, dealStartDate?: string | null, duration?: number | null) => {
      if(disableCreateButton) return;

      setDisableCreateButton(true);
      if(!dealStartDate || dealStartDate == null || dealStartDate == '' || !duration || duration == null || duration == 0) {
        message.error("Please enter Start Date and Duration in HubSpot")
        setDisableCreateButton(false);
        return
      }
        const createPricing = await createPricingDeal(dealId);
        if (createPricing.error_status === 401) {
          setTokenExpired(true);
          setDisableCreateButton(false);
        } else {
          if (createPricing.error) {
            console.error('Error Response Data:', createPricing.error);
            console.error('Error Response Status:', createPricing.error_status);
            setDisableCreateButton(false);
            // setCreateDealError(createPricing.error);
          } else {
            setDisableCreateButton(false);
            navigate(`/edit-pricing-details?dealId=${dealId}&versionName=${encodeURIComponent(createPricing.version_name)}`);
          }
        }
      }

    const handleDealRefresh = async (dealId: string) => {
      setRefreshingDeal(dealId)
      await refreshDeals(parseInt(dealId)).then((resp) => {
          if(resp.error ?? '') {
          message.error(resp.error)
          return
          }
          message.success("Successfully Refreshed the Deal");
          setRefreshingDeal('');
      })
      .catch((error) => {
          setRefreshingDeal('');
          message.error("Failed to Refresh the Deal");
      });
    }

    const fetchDealDetails = async () => {
      let deals = await getHubspotDeals();
      if (!Array.isArray(deals)) {
        message.error(deals?.error || 'Failed to load HubSpot deals');
        setDeals([]);
        setFilteredDeals([]);
        setLoading(false);
        return;
      }
      if(displayUserSpecificDeals) {
        deals = deals.filter((deal: DealData) => deal['is_owner'] === true || deal['can_create_pricing'] === true);
      }
      let pricingDeals = await getPricingDeals();
      if (!Array.isArray(pricingDeals)) {
        message.error(pricingDeals?.error || 'Failed to load pricing deals');
        pricingDeals = [];
      }
      pricingDeals = pricingDeals.map((deal: DealData) => ({ ...deal, expandable_row: true }));
      const transformedDeal: any = transformDeal([...deals, ...pricingDeals]);
      setDeals(transformedDeal);
      setFilteredDeals(transformedDeal)
      setLoading(false);
    }

    useEffect(() => {
        try {
            if (userContext === null) {
                setUserContext(JSON.parse(localStorage.getItem('user_details') as string));
            }
            fetchDealDetails();
        } catch (error) {
            setLoading(false);
            console.error(error);
        }
    }, []);

    useEffect(() => {
      try {
          if (refreshingDeal == '') {
            setLoading(true)
            fetchDealDetails();
          }
      } catch (error) {
          setLoading(false);
          console.error(error);
      }
  }, [refreshingDeal]);

    const generateFilters = (data: any[], key: string):any[] => {
        const uniqueValues = Array.from(
            new Set(data.map((item) => {
              try {
                  return item[key].toString();
              } catch (error) {
                  console.error('Error converting item to string:', { item, key, data });
                  return '';
              }
          }))
        ).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        return uniqueValues.map((value) => ({ text: value, value }));
    }

    const columns: TableProps<DataType>['columns'] = [
          {
            title: 'Action',
            key: 'action',
            align: 'right',
            render: (text, record) => (
                <>
                {!record.expandableRow ?
                    <div style={{display: 'flex', gap: '4px'}}>
                        <Tooltip title="Create Pricing">
                        <Button type="primary" size="small"
                        disabled={!record.canCreatePricing || disableCreateButton}
                            onClick={() => createDealPricing(parseInt(record.dealId),record.startDate, record.duration)}>
                            <FileAddOutlined />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Refresh Deal Now">
                        <Button size="small" onClick={() => handleDealRefresh(record.dealId)} disabled={refreshingDeal == record.dealId}>
                            <SyncOutlined spin={refreshingDeal == record.dealId}/>
                        </Button>
                        </Tooltip>
                    </div> :
                    <div style={{display: 'flex', gap: '4px'}}>
                        <Tooltip title="Compare Versions">
                            <Button size="small" type="primary" onClick={() => window.location.href = `/version-management?deal_id=${record.dealId}`}>
                                <FolderOpenOutlined />
                            </Button>
                        </Tooltip>
                        <Tooltip title="Refresh Deal Now">
                            <Button size="small" onClick={() => handleDealRefresh(record.dealId)}>
                                <SyncOutlined spin={refreshingDeal == record.dealId}/>
                            </Button>
                        </Tooltip>
                    </div>
                }
                </>
            ),
        },
        {
            title: 'Client',
            dataIndex: 'client',
            key: 'client',
            sorter: (a, b) => a.client.toLowerCase().localeCompare(b.client.toLowerCase()),
            filters: generateFilters(deals, 'client'),
            onFilter: (value, record) => record.client === value,
        },
        {
            title: 'Deal Name',
            dataIndex: 'dealName',
            key: 'dealName',
            sorter: (a, b) => a.dealName.toLowerCase().localeCompare(b.dealName.toLowerCase()),
            // render: (text, record) => <Button disabled={disableCreateButton || !record.canCreatePricing} type='text' style={{textDecoration: 'underline', color: '#0d6efd'}}
            //   onClick={() => { if(record.canCreatePricing) createDealPricing(parseInt(record.dealId), record.startDate, record.duration)}}
            // >{text}</Button>
        },
        {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'startDate',
            sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
            defaultSortOrder: 'ascend', // Set default sort order
        },
        {
            title: 'Stage',
            dataIndex: 'Stage',
            key: 'Stage',
            sorter: (a, b) => a.Stage.toLowerCase().localeCompare(b.Stage.toLowerCase()),
            filters: generateFilters(deals, 'Stage'),
            onFilter: (value, record) => record.Stage === value,
        },
        {
            title: 'Deal Amount',
            dataIndex: 'dealAmount',
            key: 'dealAmount',
            align: 'right',
            sorter: (a, b) => a.dealAmount.localeCompare(b.dealAmount),
        },
        {
            title: 'Deal Owner',
            dataIndex: 'dealOwner',
            key: 'dealOwner',
            sorter: (a, b) => a.dealOwner.toLowerCase().localeCompare(b.dealOwner.toLowerCase()),
            filters: generateFilters(deals, 'dealOwner'),
            onFilter: (value, record) => record.dealOwner === value,
        },
        {
            title: 'ODE Status',
            dataIndex: 'odeStatus',
            key: 'odeStatus',
            sorter: (a, b) => a.odeStatus.toLowerCase().localeCompare(b.odeStatus.toLowerCase()),
            filters: generateFilters(deals, 'odeStatus'),
            onFilter: (value, record) => record.odeStatus === value,
        },
        {
          title: 'Pricing Last Updated',
          dataIndex: 'updatedOn',
          key: 'updatedOn',
          sorter: (a, b) => new Date(a.updatedOn).getTime() - new Date(b.updatedOn).getTime(),
        },
        {
          title: 'HubSpot Link',
          dataIndex: 'hubspotLink',
          key: 'hubspotLink',
          align:'center',
          render: (text, record) =>
            <a target="_blank" href={record.HubSpotLink}>{record.dealId}</a>    
        },
    ];

  return (
     <Card 
     title={'All Deals'} 
     extra={
        <Search
          placeholder="Search deals"
          value={searchTerm}
          onChange={(e) => handleSearch(e)}
          allowClear
        />
     }>
        <Table
        className='custom-table'
        dataSource={filteredDeals}
        columns={columns}
        scroll={{ x: 'max-content' }}
        loading={loading}
        expandable={{
            expandedRowRender: (record, index, indent, expanded) => <p style={{ margin: 0 }}><Row gutter={16}><DealEconomicsSummariesTable dealDetails={record} expanded={expanded} /></Row></p>,
            rowExpandable: (record) => record.key !== '' && record.expandableRow,
            expandedRowKeys: expandedRowKeys,
            onExpand: (expanded, record) => {
                if (expanded) {
                    setExpandedRowKeys([...expandedRowKeys, record.key])
                } else {
                    setExpandedRowKeys(expandedRowKeys.filter((k) => k != record.key))
                }
            }
        }}
        />
     </Card>
    
  )
}

export default AllPricingDealsTable;
